import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { NavBar } from '../components/NavBar';
import { 
  Watch,
  Activity,
  Heart,
  Moon,
  Footprints,
  Flame,
  RefreshCw,
  Check,
  Loader2,
  Smartphone,
  Link2,
  Unlink,
  Zap,
  TrendingUp,
  Bluetooth,
  Radio,
  X,
  AlertCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Bluetooth Service UUIDs
const HEART_RATE_SERVICE = 'heart_rate';
const HEART_RATE_MEASUREMENT = 'heart_rate_measurement';
const BATTERY_SERVICE = 'battery_service';
const BATTERY_LEVEL = 'battery_level';

export const WearablesPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [bluetoothSupported, setBluetoothSupported] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [connectedDevice, setConnectedDevice] = useState(null);
  const [deviceData, setDeviceData] = useState({
    heartRate: null,
    batteryLevel: null,
    deviceName: null,
    connected: false
  });
  const [heartRateHistory, setHeartRateHistory] = useState([]);
  const characteristicRef = useRef(null);
  const deviceRef = useRef(null);

  useEffect(() => {
    // Check if Web Bluetooth is supported
    if (!navigator.bluetooth) {
      setBluetoothSupported(false);
    }
    
    // Cleanup function - inline to avoid dependency issues
    return () => {
      if (characteristicRef.current) {
        try {
          characteristicRef.current.stopNotifications();
        } catch (e) {
          console.debug('BLE cleanup:', e.message);
        }
      }
      if (deviceRef.current && deviceRef.current.gatt?.connected) {
        deviceRef.current.gatt.disconnect();
      }
    };
  }, []);

  const scanForDevices = async () => {
    if (!navigator.bluetooth) {
      toast.error('Bluetooth is not supported in this browser. Please use Chrome or Edge.');
      return;
    }

    setScanning(true);
    
    try {
      // Request a Bluetooth device with heart rate service
      const device = await navigator.bluetooth.requestDevice({
        filters: [
          { services: [HEART_RATE_SERVICE] },
          { services: ['heart_rate'] },
        ],
        optionalServices: [BATTERY_SERVICE, 'battery_service', 'device_information']
      });

      toast.success(`Found device: ${device.name || 'Unknown Device'}`);
      
      // Connect to the device
      await connectToDevice(device);
      
    } catch (error) {
      if (error.name === 'NotFoundError') {
        toast.error('No compatible devices found. Make sure your device is in pairing mode.');
      } else if (error.name === 'SecurityError') {
        toast.error('Bluetooth access denied. Please allow Bluetooth access.');
      } else {
        toast.error('Failed to scan for devices. Please try again.');
      }
    }
    
    setScanning(false);
  };

  const connectToDevice = async (device) => {
    try {
      setLoading(true);
      deviceRef.current = device;
      
      // Listen for disconnection
      device.addEventListener('gattserverdisconnected', onDisconnected);
      
      // Connect to GATT server
      const server = await device.gatt.connect();
      
      setConnectedDevice(device);
      setDeviceData(prev => ({
        ...prev,
        deviceName: device.name || 'Fitness Device',
        connected: true
      }));
      
      toast.success(`Connected to ${device.name || 'Fitness Device'}!`);
      
      // Get Heart Rate Service
      try {
        const heartRateService = await server.getPrimaryService(HEART_RATE_SERVICE);
        const heartRateChar = await heartRateService.getCharacteristic(HEART_RATE_MEASUREMENT);
        
        characteristicRef.current = heartRateChar;
        
        // Start notifications for heart rate
        await heartRateChar.startNotifications();
        heartRateChar.addEventListener('characteristicvaluechanged', handleHeartRateChange);
        
        // Heart rate service may not be available on all devices
      } catch (e) {
        console.debug('Heart rate service unavailable on this device:', e?.name || e);
      }
      
      // Get Battery Service (if available)
      try {
        const batteryService = await server.getPrimaryService(BATTERY_SERVICE);
        const batteryChar = await batteryService.getCharacteristic(BATTERY_LEVEL);
        const batteryValue = await batteryChar.readValue();
        const batteryLevel = batteryValue.getUint8(0);
        
        setDeviceData(prev => ({
          ...prev,
          batteryLevel: batteryLevel
        }));
      } catch (e) {
        console.debug('Battery service unavailable on this device:', e?.name || e);
      }
      
    } catch (error) {
      toast.error('Failed to connect to device. Please try again.');
      setConnectedDevice(null);
    }
    
    setLoading(false);
  };

  const handleHeartRateChange = (event) => {
    const value = event.target.value;
    // Heart rate measurement format
    // First byte: flags, Second byte: heart rate value (if 8-bit)
    const flags = value.getUint8(0);
    let heartRate;
    
    if (flags & 0x01) {
      // 16-bit heart rate
      heartRate = value.getUint16(1, true);
    } else {
      // 8-bit heart rate
      heartRate = value.getUint8(1);
    }
    
    setDeviceData(prev => ({
      ...prev,
      heartRate: heartRate
    }));
    
    // Add to history (keep last 30 readings)
    setHeartRateHistory(prev => {
      const newHistory = [...prev, { time: new Date(), value: heartRate }];
      return newHistory.slice(-30);
    });
  };

  const onDisconnected = () => {
    toast.info('Device disconnected');
    setConnectedDevice(null);
    setDeviceData({
      heartRate: null,
      batteryLevel: null,
      deviceName: null,
      connected: false
    });
    characteristicRef.current = null;
  };

  const disconnectDevice = async () => {
    if (characteristicRef.current) {
      try {
        await characteristicRef.current.stopNotifications();
        characteristicRef.current.removeEventListener('characteristicvaluechanged', handleHeartRateChange);
      } catch (e) {
        console.debug('stopNotifications failed, continuing disconnect:', e?.name || e);
      }
    }
    
    if (deviceRef.current && deviceRef.current.gatt.connected) {
      deviceRef.current.gatt.disconnect();
    }
    
    setConnectedDevice(null);
    setDeviceData({
      heartRate: null,
      batteryLevel: null,
      deviceName: null,
      connected: false
    });
    setHeartRateHistory([]);
    characteristicRef.current = null;
    deviceRef.current = null;
    
    toast.success('Device disconnected');
  };

  const getHeartRateZone = (hr) => {
    if (!hr) return { zone: 'Unknown', color: 'text-gray-400', bg: 'bg-gray-500/20' };
    if (hr < 100) return { zone: 'Resting', color: 'text-blue-400', bg: 'bg-blue-500/20' };
    if (hr < 120) return { zone: 'Light', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (hr < 140) return { zone: 'Moderate', color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    if (hr < 160) return { zone: 'Hard', color: 'text-orange-400', bg: 'bg-orange-500/20' };
    return { zone: 'Maximum', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  const heartRateZone = getHeartRateZone(deviceData.heartRate);

  return (
    <div className="min-h-screen bg-black" data-testid="wearables-page">
      <NavBar title="Wearables" />

      <main className="container mx-auto px-6 py-8">
        {/* Page Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 text-center"
        >
          <div className="inline-flex items-center justify-center h-16 w-16 bg-primary/20 mb-4">
            <Bluetooth className="h-8 w-8 text-primary" />
          </div>
          <h2 className="font-heading text-3xl text-white uppercase tracking-wider mb-2">
            Bluetooth <span className="text-primary">Pairing</span>
          </h2>
          <p className="text-muted-foreground max-w-md mx-auto">
            Connect your fitness tracker via Bluetooth for real-time heart rate monitoring
          </p>
        </motion.div>

        {/* Browser Support Warning */}
        {!bluetoothSupported && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-red-500/20 border border-red-500/50 p-4 mb-8 flex items-center gap-4"
          >
            <AlertCircle className="h-6 w-6 text-red-400 flex-shrink-0" />
            <div>
              <p className="text-white font-medium">Bluetooth Not Supported</p>
              <p className="text-red-200 text-sm">
                Your browser doesn't support Web Bluetooth. Please use Chrome or Edge on desktop, or Chrome on Android.
              </p>
            </div>
          </motion.div>
        )}

        {/* Connection Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`bg-card border-2 overflow-hidden mb-8 ${
            deviceData.connected ? 'border-green-500' : 'border-border'
          }`}
        >
          {deviceData.connected && (
            <div className="bg-green-500 text-white text-center py-2 text-sm font-medium flex items-center justify-center gap-2">
              <Radio className="h-4 w-4 animate-pulse" /> Connected to {deviceData.deviceName}
            </div>
          )}
          
          <div className="p-8">
            {!deviceData.connected ? (
              /* Not Connected State */
              <div className="text-center">
                <div className="h-24 w-24 bg-primary/20 flex items-center justify-center mx-auto mb-6">
                  <Bluetooth className="h-12 w-12 text-primary" />
                </div>
                <h3 className="font-heading text-2xl text-white uppercase tracking-wider mb-2">
                  Pair Your Device
                </h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Make sure your fitness tracker is in pairing mode and within range. 
                  Compatible with heart rate monitors and fitness bands.
                </p>
                
                <Button
                  onClick={scanForDevices}
                  disabled={scanning || !bluetoothSupported}
                  className="bg-primary text-black hover:bg-primary/90 h-14 px-8 text-lg"
                  data-testid="scan-bluetooth-btn"
                >
                  {scanning ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Scanning...
                    </>
                  ) : (
                    <>
                      <Bluetooth className="h-5 w-5 mr-2" />
                      Scan for Devices
                    </>
                  )}
                </Button>
                
                <div className="mt-8 grid md:grid-cols-3 gap-4 text-left">
                  <div className="bg-white/5 p-4">
                    <div className="text-2xl mb-2">1️⃣</div>
                    <p className="text-white font-medium">Enable Bluetooth</p>
                    <p className="text-muted-foreground text-sm">Turn on Bluetooth on your device</p>
                  </div>
                  <div className="bg-white/5 p-4">
                    <div className="text-2xl mb-2">2️⃣</div>
                    <p className="text-white font-medium">Pairing Mode</p>
                    <p className="text-muted-foreground text-sm">Put your tracker in pairing mode</p>
                  </div>
                  <div className="bg-white/5 p-4">
                    <div className="text-2xl mb-2">3️⃣</div>
                    <p className="text-white font-medium">Click Scan</p>
                    <p className="text-muted-foreground text-sm">Select your device from the list</p>
                  </div>
                </div>
              </div>
            ) : (
              /* Connected State - Live Data */
              <div>
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Heart Rate Display */}
                  <div className={`${heartRateZone.bg} border border-white/10 p-6 text-center`}>
                    <Heart className={`h-12 w-12 ${heartRateZone.color} mx-auto mb-4 ${deviceData.heartRate ? 'animate-pulse' : ''}`} />
                    <div className="text-6xl font-bold text-white mb-2">
                      {deviceData.heartRate || '--'}
                      <span className="text-2xl text-muted-foreground ml-2">BPM</span>
                    </div>
                    <Badge className={`${heartRateZone.bg} ${heartRateZone.color}`}>
                      {heartRateZone.zone} Zone
                    </Badge>
                  </div>
                  
                  {/* Device Info */}
                  <div className="space-y-4">
                    <div className="bg-white/5 p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Watch className="h-6 w-6 text-primary" />
                        <div>
                          <p className="text-white font-medium">{deviceData.deviceName}</p>
                          <p className="text-muted-foreground text-sm">Connected Device</p>
                        </div>
                      </div>
                      <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                    </div>
                    
                    {deviceData.batteryLevel !== null && (
                      <div className="bg-white/5 p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Zap className="h-6 w-6 text-yellow-400" />
                          <div>
                            <p className="text-white font-medium">Battery Level</p>
                            <p className="text-muted-foreground text-sm">Device battery</p>
                          </div>
                        </div>
                        <span className="text-2xl font-bold text-white">{deviceData.batteryLevel}%</span>
                      </div>
                    )}
                    
                    <div className="bg-white/5 p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <Activity className="h-6 w-6 text-blue-400" />
                        <p className="text-white font-medium">Heart Rate History</p>
                      </div>
                      <div className="flex items-end gap-1 h-16">
                        {heartRateHistory.slice(-20).map((reading) => (
                          <div
                            key={reading.timestamp}
                            className="bg-primary flex-1 transition-all"
                            style={{ height: `${Math.min(100, (reading.value / 200) * 100)}%` }}
                          />
                        ))}
                        {heartRateHistory.length === 0 && (
                          <p className="text-muted-foreground text-sm">Waiting for data...</p>
                        )}
                      </div>
                    </div>
                    
                    <Button
                      onClick={disconnectDevice}
                      variant="outline"
                      className="w-full text-red-400 border-red-400/50 hover:bg-red-500/10"
                    >
                      <Unlink className="h-4 w-4 mr-2" />
                      Disconnect Device
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Compatible Devices */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-card border border-border p-6"
        >
          <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4">
            Compatible Devices
          </h3>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="bg-white/5 p-4 text-center">
              <div className="text-3xl mb-2">💓</div>
              <p className="text-white font-medium">Heart Rate Monitors</p>
              <p className="text-muted-foreground text-xs">Polar, Garmin, Wahoo</p>
            </div>
            <div className="bg-white/5 p-4 text-center">
              <div className="text-3xl mb-2">⌚</div>
              <p className="text-white font-medium">Fitness Bands</p>
              <p className="text-muted-foreground text-xs">Mi Band, Amazfit, Honor</p>
            </div>
            <div className="bg-white/5 p-4 text-center">
              <div className="text-3xl mb-2">🏃</div>
              <p className="text-white font-medium">Running Watches</p>
              <p className="text-muted-foreground text-xs">Garmin, Coros, Suunto</p>
            </div>
            <div className="bg-white/5 p-4 text-center">
              <div className="text-3xl mb-2">🚴</div>
              <p className="text-white font-medium">Cycling Sensors</p>
              <p className="text-muted-foreground text-xs">Heart rate chest straps</p>
            </div>
          </div>
          
          <div className="mt-6 bg-primary/10 border border-primary/30 p-4">
            <p className="text-white text-sm">
              <strong>Note:</strong> Web Bluetooth works in Chrome and Edge browsers on desktop, and Chrome on Android. 
              Safari and Firefox do not support Web Bluetooth. Your device must support the Bluetooth Heart Rate Profile.
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  );
};
